/** Guest UI locale for the public landing (Phase 44C slice). */

export type GuestLocale = 'en' | 'ru' | 'uk' | 'pl'

export const GUEST_LOCALE_STORAGE_KEY = 'lexio.guestUiLocale'

export const GUEST_LOCALES: ReadonlyArray<{
  code: GuestLocale
  label: string
  nativeLabel: string
  flag: string
}> = [
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский', flag: '🇷🇺' },
  { code: 'uk', label: 'Ukrainian', nativeLabel: 'Українська', flag: '🇺🇦' },
  { code: 'pl', label: 'Polish', nativeLabel: 'Polski', flag: '🇵🇱' },
]

const SUPPORTED = new Set<string>(GUEST_LOCALES.map((l) => l.code))

export function isGuestLocale(value: string | null | undefined): value is GuestLocale {
  return Boolean(value && SUPPORTED.has(value))
}

/** Map BCP-47 tags (uk-UA, ru-RU, pl, en-GB…) to a supported guest locale. */
export function matchGuestLocale(tag: string): GuestLocale | null {
  const normalized = tag.trim().toLowerCase().replace('_', '-')
  if (!normalized) return null
  if (isGuestLocale(normalized)) return normalized
  const base = normalized.split('-')[0]
  if (isGuestLocale(base)) return base
  return null
}

export function detectBrowserLocale(
  languages: readonly string[] = typeof navigator !== 'undefined'
    ? [navigator.language, ...(navigator.languages ?? [])]
    : ['en'],
): GuestLocale {
  for (const tag of languages) {
    const matched = matchGuestLocale(tag)
    if (matched) return matched
  }
  return 'en'
}

export function readStoredGuestLocale(): GuestLocale | null {
  try {
    const raw = localStorage.getItem(GUEST_LOCALE_STORAGE_KEY)
    return isGuestLocale(raw) ? raw : null
  } catch {
    return null
  }
}

export function resolveGuestLocale(): GuestLocale {
  return readStoredGuestLocale() ?? detectBrowserLocale()
}

export function applyDocumentLang(locale: GuestLocale): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = locale
}

type Listener = () => void

let currentLocale: GuestLocale | null = null
const listeners = new Set<Listener>()

function ensureInitialized(): GuestLocale {
  if (currentLocale == null) {
    currentLocale = resolveGuestLocale()
    applyDocumentLang(currentLocale)
  }
  return currentLocale
}

export function getGuestLocale(): GuestLocale {
  return ensureInitialized()
}

export function setGuestLocale(locale: GuestLocale): void {
  ensureInitialized()
  try {
    localStorage.setItem(GUEST_LOCALE_STORAGE_KEY, locale)
  } catch {
    /* private mode — still update in-memory */
  }
  applyDocumentLang(locale)
  currentLocale = locale
  listeners.forEach((listener) => listener())
}

export function subscribeGuestLocale(listener: Listener): () => void {
  ensureInitialized()
  listeners.add(listener)
  return () => listeners.delete(listener)
}
